import { useIntl } from "react-intl";

import { ChatRow } from "./components/chatRow";
import type { TasOccurrenceChatController } from "../../../../hooks/useTasOccurrenceChat";
import ConfirmDialog from "../../../../components/dialogs/ConfirmDialog";

interface TasOccurrenceChatProps extends TasOccurrenceChatController {
  disabled: boolean | undefined;
}

export const ChatOccurrenceTas = ({
  disabled,
  newCommentText,
  setNewCommentText,
  comments,
  loading,
  submittingNew,
  editingCommentId,
  editDraft,
  savingEditUuid,
  deletingId,
  showConfirmDialog,
  commentToDelete,
  setEditDraft,
  submitNewComment,
  beginEdit,
  cancelEdit,
  saveEdit,
  removeComment,
  setCommentToDelete,
  setShowConfirmDialog,
}: TasOccurrenceChatProps) => {
  const intl = useIntl();

  return (
    <div className="border border-gray-300 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 p-8">
      <h3 className="text-size-14 font-semibold text-color-dark mb-12">
        {intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.title" })}
      </h3>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-color-dark">
          <div className="spinner mr-3">
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
            <div />
          </div>
          {intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.loading" })}
        </div>
      ) : (
        <>
          {!disabled && (
            <div className={`pb-6 ${comments.length > 0 ? "mb-6 border-b border-gray-300 dark:border-neutral-700" : ""}`}>
              <div className="text-size-14 lowercase text-color-status-resting mb-2">
                {intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.submit-comment" })}
              </div>
              <textarea
                className="form-control w-full min-h-[120px] resize-y mb-3"
                placeholder={intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.placeholder" })}
                value={newCommentText}
                onChange={(e) => setNewCommentText(e.target.value)}
                disabled={submittingNew}
              />
              <div className="flex justify-end">
                <button
                  type="button"
                  className={`btn btn-primary ${submittingNew ? "btn-loading disabled" : ""}`}
                  onClick={() => void submitNewComment()}
                  disabled={submittingNew || !newCommentText.trim()}
                >
                  {intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.submit-comment" })}
                </button>
              </div>
            </div>
          )}
          <div>
            {comments.map((comment) => (
              <ChatRow
                key={comment.id}
                comment={comment}
                isEditing={editingCommentId === comment.id}
                editDraft={editDraft}
                onEditDraftChange={setEditDraft}
                onStartEdit={() => beginEdit(comment)}
                onCancelEdit={cancelEdit}
                onSaveEdit={() => void saveEdit()}
                onDelete={() => {
                  setCommentToDelete(comment);
                  setShowConfirmDialog(true);
                }}
                savingEdit={savingEditUuid === comment.id}
                deleting={deletingId === comment.id}
                disabled={disabled}
              />
            ))}
          </div>
        </>
      )}

      <ConfirmDialog
        show={showConfirmDialog}
        title={intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.confirm-delete-title" })}
        body={intl.formatMessage({ id: "intl-msg:tas.occurrence.comments.confirm-delete-body" })}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          if (commentToDelete) {
            void removeComment(commentToDelete.id);
            setShowConfirmDialog(false);
            setCommentToDelete(null);
          }
        }}
      />
    </div>
  );
};
